class KlaviyoBackInStock extends HTMLElement {
  constructor() {
    super();
    this.form = this.querySelector('form');
    this.submitButton = this.querySelector('[type="submit"]');
  }

  connectedCallback() {
    this.onFormSubmit = this.onFormSubmit.bind(this);
    this.removeMessage = this.removeMessage.bind(this);
    if (this.form) {
      this.form.addEventListener('submit', this.onFormSubmit);
      this.form.elements.phone.addEventListener('input', (event) => event.target.setCustomValidity(''));
    }
  }

  // Klaviyo only accepts E.164 numbers; national numbers without a country code are assumed French.
  static toE164(value) {
    const cleaned = value.replace(/[\s.()-]/g, '').replace(/^00/, '+');
    const phone = cleaned.startsWith('0') ? `+33${cleaned.slice(1)}` : cleaned;
    return /^\+[1-9]\d{7,14}$/.test(phone) ? phone : null;
  }

  // Sent separately from the back in stock subscription so a phone number rejected by Klaviyo never blocks the email alert.
  updateProfilePhone(companyId, email, phone) {
    return fetch(`https://a.klaviyo.com/client/profiles/?company_id=${companyId}`, {
      method: 'POST',
      body: JSON.stringify({
        data: {
          type: 'profile',
          attributes: { email, phone_number: phone },
        },
      }),
      headers: {
        accept: 'application/json',
        'content-type': 'application/json',
        Revision: '2024-07-15',
      },
    });
  }

  disconnectedCallback() {
    if (this.form) this.form.removeEventListener('submit', this.onFormSubmit);
  }

  removeMessage() {
    this.querySelector('[data-message-success]').classList.add('hidden');
    this.querySelector('[data-message-error]').classList.add('hidden');
  }

  setLoadingState(isLoading) {
    this.submitButton.classList.toggle('loading', isLoading);
    const spinner = this.submitButton.querySelector('.loading__spinner');
    if (spinner) spinner.classList.toggle('hidden', !isLoading);
  }

  async onFormSubmit(event) {
    event.preventDefault();

    const phoneInput = this.form.elements.phone;
    const phone = phoneInput.value.trim() ? KlaviyoBackInStock.toE164(phoneInput.value) : '';
    if (phone === null) {
      phoneInput.setCustomValidity(phoneInput.dataset.invalidMessage);
      phoneInput.reportValidity();
      return;
    }

    this.removeMessage();
    this.setLoadingState(true);
    const formData = new FormData(event.target);

    const backInStockSubscription = async () => {
      return fetch(event.target.getAttribute('action'), {
        method: event.target.getAttribute('method'),
        body: JSON.stringify({
          data: {
            type: 'back-in-stock-subscription',
            attributes: {
              channels: ['EMAIL'],
              profile: {
                data: {
                  type: 'profile',
                  attributes: {
                    email: formData.get('email'),
                  },
                },
              },
            },
            relationships: {
              variant: {
                data: {
                  id: `$shopify:::$default:::${formData.get('variant')}`,
                  type: 'catalog-variant',
                },
              },
            },
          },
        }),
        headers: {
          accept: 'application/json',
          'content-type': 'application/json',
          Revision: '2024-07-15',
        },
      });
    };

    const newsletterSubscription = async () => {
      if (formData.get('subscribe_for_newsletter') === 'on') {
        return fetch(`https://a.klaviyo.com/client/subscriptions/?company_id=${formData.get('a')}`, {
          method: 'POST',
          body: JSON.stringify({
            data: {
              attributes: {
                profile: {
                  data: {
                    type: 'profile',
                    attributes: {
                      email: formData.get('email'),
                    },
                  },
                },
              },
              relationships: {
                list: {
                  data: {
                    id: formData.get('g'),
                    type: 'list',
                  },
                },
              },
              type: 'subscription',
            },
          }),
          headers: {
            accept: 'application/json',
            'content-type': 'application/json',
            Revision: '2024-07-15',
          },
        });
      } else {
        // Resolve immediately if newsletter subscription is not needed
        return Promise.resolve({ status: 200 });
      }
    };

    try {
      // Execute all fetch calls concurrently
      const results = await Promise.all([backInStockSubscription(), newsletterSubscription()]);

      // Check if all fetch calls were successful
      if (!results.every((res) => res.status === 200 || res.status === 202)) {
        throw new Error('One or more API calls failed to complete successfully');
      }

      this.querySelector('[data-message-success]').classList.remove('hidden');
      if (phone) this.updateProfilePhone(formData.get('a'), formData.get('email'), phone).catch(console.error);
    } catch (err) {
      console.error(err);
      this.querySelector('[data-message-error]').classList.remove('hidden');
    } finally {
      this.setLoadingState(false);
    }
  }
}
customElements.define('klaviyo-back-in-stock', KlaviyoBackInStock);
